/**
 * City configuration for Waypo.
 *
 * Cities may be public (fully launched), soft-launched (accessible by direct
 * URL but hidden from tourist-facing discovery), or commented out (pending).
 *
 * The `name` must match exactly what Google's Geocoding API returns for the
 * `locality` component (e.g. "Melbourne", "Sydney", "London") — this is
 * what gets stored on Tour.city when a tour is published.
 *
 * The first city in the list is used as the landing page fallback when
 * GeoIP detection fails or returns an unlaunched city.
 */

export interface CityHero {
  /** One or more hero background images for this city (relative to /public).
   *  See getRandomHeroImage() to select one for display. */
  images: string[]
}

export interface City {
  name: string
  /**
   * When true the city is fully public: it appears in the city selector,
   * GeoIP detection, and the sitemap.
   *
   * When false (or omitted) the city is in soft-launch mode: Local Experts
   * can create tours and the city page is reachable via direct URL, but the
   * city is hidden from all tourist-facing discovery surfaces.
   */
  public?: boolean
  hero?: CityHero
}

/** Fallback hero used for cities that don't yet have a configured image. */
export const DEFAULT_CITY_HERO: CityHero = {
  images: [],
}

/** All cities in any launch state (soft or public). Used for routing. */
export const CITIES: City[] = [
  { name: 'Melbourne', public: true, hero: { images: ['/images/hero-melbourne.jpg'] } },
  { name: 'Paris', hero: { images: ['/images/hero-paris.jpg'] } },
  { name: 'New York', hero: { images: ['/images/hero-new_york.jpg'] } },
  { name: 'London', hero: { images: ['/images/hero-london.jpg'] } },
  // { name: 'Sydney' },
]

/** Returns a random hero image path for the given hero config, or null if none are configured. */
export function getRandomHeroImage(hero: CityHero): string | null {
  if (!hero.images.length) return null
  return hero.images[Math.floor(Math.random() * hero.images.length)]
}

export function cityToSlug(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-')
}

export function getCityBySlug(slug: string): City | undefined {
  return CITIES.find((c) => cityToSlug(c.name) === slug)
}

/** Cities visible to tourists: shown in the city selector, used for GeoIP
 *  fallback, and included in the sitemap. Excludes soft-launch cities. */
export function getPublicCities(): City[] {
  return CITIES.filter((c) => c.public === true)
}

/** Cities that are configured but not yet publicly launched. Reachable via
 *  direct URL; hidden from GeoIP and sitemap. */
export function getSoftLaunchCities(): City[] {
  return CITIES.filter((c) => c.public !== true)
}
